import React from "react";

import { IgCodeEditor, IgDivider } from "@intelligems/ig-theme";
import { styled } from "@mui/material";
import Stack from "@mui/material/Stack";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import { LayoutMod } from "@src/store/slices/experienceBuilder/types";
import { assert } from "@src/utils/assert";
import { ElementSelector } from "@src/features/experienceBuilder/modifications/basicMods/ElementSelector";
import { ElementMeta } from "@src/features/experienceBuilder/modifications/common/panel/ElementMeta";

const CodeEditorWrapper = styled("div")(({ theme }) => ({
  border: `1px solid ${theme.igBorders.core.default}`,
  borderRadius: "6px",
  flex: 1,
  minHeight: "200px",
  display: "flex",
  flexDirection: "column",
  '& > [class*="cm-theme"]': {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  "& .cm-editor": {
    flex: 1,
    minHeight: 0,
  },
}));

export const LayoutPanel = () => {
  const dispatch = useDispatch();

  const mod = useSelector(Selector.basicMod.getVariationDraftHTML);
  assert(mod !== undefined, "mod is undefined");

  const isApplicable = useSelector(Selector.isVariationDraftApplicable);
  const errors = useSelector(Selector.getVariationDraftUIErrors);

  const update = (update: Partial<LayoutMod>) =>
    dispatch(Action.basicMod.setDraft({ ...mod, ...update }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  return (
    <>
      <ModificationHeader />
      <Panel>
        <Panel.Body>
          <ElementMeta mod={mod} errors={errors} onChange={update} />

          <IgDivider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Panel.Header>Element change</Panel.Header>
            <Panel.Checkbox
              label="Hide element"
              checked={mod.remove}
              onChange={(remove) => update({ remove })}
            />
          </Stack>

          <CodeEditorWrapper>
            <IgCodeEditor
              language="html"
              value={mod.html}
              onChange={(html) => update({ html })}
            />
          </CodeEditorWrapper>
        </Panel.Body>
        <Panel.Buttons>
          <Panel.CancelButton onClick={cancel} />
          <Panel.ApplyButton onClick={apply} disabled={!isApplicable} />
        </Panel.Buttons>
      </Panel>
    </>
  );
};
